import { CommonModule } from '@angular/common';
import { Component, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth-service';
import { AESService } from '../../services/aes.service';

@Component({
  selector: 'app-header',
  imports: [CommonModule, NgbDropdownModule],
  templateUrl: './header.html',
  styleUrl: './header.css',
})
export class HeaderComponent implements OnInit {

  @Output() toggleMobileMenu = new EventEmitter<void>();
  // Profile menu: open the Change Password popup
  @Output() changePassword = new EventEmitter<void>();
  private router = inject(Router);
  private authService = inject(AuthService);
  private aes = inject(AESService);

  username: string = '';
  email: string = '';
  roleLabel: string = '';
  // Profile menu (user_details.user): clinic and organisation, when present
  clinicName = '';
  orgName = '';
  // "Welcome, <name>" for doctors; RSSDI for other roles
  greetingName = 'RSSDI';
  loggedInUser: any;
  isLoggingOut = false;

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      const data = localStorage.getItem('user_details');
      const localData = data ? JSON.parse(data) : null;
      if (localData?.user) {
        const user = localData.user;
        this.loggedInUser = user;
        // name, username (the login) and clinicName are stored encrypted
        const name = this.readText(user?.name);
        const login = this.readText(user?.username);
        const email = this.readText(user?.email);
        this.roleLabel = this.toRoleLabel(user?.roleName);
        this.username = name || login || this.roleLabel || 'User';
        // Email ID: the email, else the login when it is an email address
        this.email = [email, login, name].find(value => value.includes('@')) || '';
        this.clinicName = this.readText(user?.clinicName ?? user?.doctorClinicName);
        this.orgName = this.readText(user?.organisationName ?? user?.organizationName ?? user?.orgName);
        if (String(user?.roleName || '').toUpperCase() === 'DOCTOR') {
          this.greetingName = this.username;
        }
      }
    }
  }

  get initials(): string {
    const parts = this.username.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
    if (!parts.length) return '';
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (parts[0].charAt(0) + last).toUpperCase();
  }

  logout() {
    if (this.isLoggingOut) {
      return;
    }
    const session = JSON.parse(localStorage?.getItem('user_details') || '{}');

    const payload = {
      userId: session?.user?.id || 0,
      refreshToken: session?.tokens?.refreshToken || ''
    };
    this.isLoggingOut = true;
    this.authService.UserLogout(payload).subscribe({
      next: () => this.endSession(),
      error: (error) => {
        // e.g. an expired session: still sign out on this device
        console.error('Logout failed:', error);
        this.endSession();
      }
    });
  }

  private endSession(): void {
    localStorage?.removeItem('user_details');
    this.isLoggingOut = false;
    this.router.navigate(['/login']);
  }

  // "DOCTOR" -> "Doctor", "SUPER_ADMIN" -> "Super Admin"
  private toRoleLabel(roleName: any): string {
    return String(roleName || '')
      .toLowerCase()
      .split(/[_\s]+/)
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // A value stored encrypted (AES, base64) or as plain text (e.g. organisationName "RSSDI").
  // Only ciphertext is decrypted: decrypting plain text can return noise.
  private readText(value: any): string {
    const text = String(value ?? '').trim();
    const looksEncrypted = text.length >= 24 && text.length % 4 === 0 && /^[A-Za-z0-9+/]+={0,2}$/.test(text);
    return looksEncrypted ? this.safeDecrypt(text) : text;
  }

  private safeDecrypt(value: any): string {
    if (!value) return '';
    try {
      return String(this.aes.decrypt(value) || '').trim();
    } catch {
      return '';
    }
  }
}
